"use client";

import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, History } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { Card, CardContent } from "@/app/components/ui/card";
import { QuickAddSelect } from "@/components/shared/quick-add-select";
import { useLocations } from "@/hooks/use-locations";
import { WorkHistoryView } from "../components/work-history-view";

function parseNumericParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function WorkHistoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialLocationId = useMemo(
    () => parseNumericParam(searchParams.get("location")),
    [searchParams]
  );
  const initialZoneId = useMemo(
    () => parseNumericParam(searchParams.get("zone")),
    [searchParams]
  );
  const initialSpotId = useMemo(
    () => parseNumericParam(searchParams.get("spot")),
    [searchParams]
  );

  const [locationId, setLocationId] = useState<number | null>(initialLocationId ?? null);

  useEffect(() => {
    setLocationId(initialLocationId ?? null);
  }, [initialLocationId]);

  const { data: locationsData, isLoading } = useLocations({ page_size: 1000 });

  const locations = locationsData?.results ?? [];
  const selected = locations.find((l) => l.id === locationId) ?? null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/work")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Work history</h1>
          <p className="text-sm text-muted-foreground">
            Pick a location, browse the zone tree, and see completed work at any node.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Location</label>
            <QuickAddSelect
              value={selected}
              onChange={(location) => setLocationId(location?.id ?? null)}
              options={locations}
              getOptionValue={(l) => l.id}
              getOptionLabel={(l) => l.name}
              isLoading={isLoading}
              placeholder="Select a location"
            />
          </div>
        </CardContent>
      </Card>

      {locationId ? (
        <WorkHistoryView
          locationId={locationId}
          initialZoneId={initialZoneId}
          initialSpotId={initialSpotId}
          showHeader={false}
        />
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
          <History className="h-8 w-8 text-muted-foreground mb-3" />
          <h3 className="font-medium">Select a location</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a location to browse work history by zone or spot.
          </p>
        </div>
      )}
    </div>
  );
}
